import { addYears, differenceInWeeks, format, isToday } from 'date-fns';
import { enCA, frCA } from 'date-fns/locale';
import { TOptions } from 'i18next';
import { FieldErrors, FieldValues } from 'react-hook-form';
import {
    AddressChangeResp,
    FormDataUpdate,
    FormDataValidation,
    PhotoCardFormState,
} from '~/components/address-change/type';
import { PageError } from '~/components/page-errors';
import { ProductDetailsFormState } from '~/components/profile/product-details-form';
import { HARDSTOP_ERROR_CODES } from '~/constants';
import {
    CELLPHONE_REGEX,
    DRIVER_LICENSE_PATTERN,
    HEALTH_CARD_PATTERN,
    INVALID_CHARACTERS_REGEX,
    OFFENCE_NUMBER_FORMAT_REGEX,
    PHOTO_CARD_PATTERN,
    POSTAL_CODE_FORMAT,
} from '~/constants/regex';
import { defaultLocale } from '~/locale/defaults';
import { DriverLicense, HealthCard, Products, VehiclePermit } from '~/models/profile';
import { ServiceType } from '~/models/renewal-reminders';
import { AddressChange, ProductTypeEnum, TPCAddressInfo } from '~/store/types';

export const urlWithBanner = ['/dashboard', '/products/drivers-licence', '/products/photo-card'];

const shouldShowStatusBanner = (url: string) => {
    return urlWithBanner.includes(url);
};

export const urlWithTrilliumBgContainer = ['/products/drivers-licence', '/products/photo-card'];

const shouldShowTrilliumBgContainer = (url: string) => {
    return urlWithTrilliumBgContainer.includes(url);
};

const shouldHideAppBar = (url: string) => {
    return url && url.match(/^\/splash\/?$/i);
};

const shouldShowMenuButton = (url: string) => {
    return url && !url.match(/^\/create-account\/?/i);
};

const isDashboardPage = (url: string) => {
    return url === '/dashboard';
};

const mapFormToPageErrors = (formErrors: FieldErrors<FieldValues>) => {
    if (!formErrors) {
        return [];
    }
    return Object.getOwnPropertyNames(formErrors).map(
        key =>
            ({
                errorCode: formErrors[key]?.message,
                formElementRef: formErrors[key]?.ref,
            }) as PageError
    );
};

const mapFormToPageErrorsNested = (
    formErrors: FieldErrors<FieldValues>,
    visited = new WeakSet()
) => {
    if (!formErrors || visited.has(formErrors)) {
        return [];
    }
    visited.add(formErrors);
    return Object.getOwnPropertyNames(formErrors).reduce<PageError[]>((acc, key) => {
        const value = formErrors[key];
        if (value) {
            if (
                value.message &&
                typeof value.message === 'string' &&
                value.ref &&
                typeof value.ref === 'object'
            ) {
                acc.push({ errorCode: value?.message, formElementRef: value?.ref as any });
            } else {
                const nested = mapFormToPageErrorsNested(value as any);
                acc.push(...nested);
            }
        }
        return acc;
    }, []);
};

const mapFormToPageErrorsWithArrayValues = (formErrors: FieldErrors<FieldValues>): PageError[] => {
    if (!formErrors) {
        return [];
    }
    return Object.getOwnPropertyNames(formErrors).reduce<PageError[]>((acc, key) => {
        const value = formErrors[key];
        if (Array.isArray(value)) {
            const arrayErrors = value.reduce((res, item) => {
                return [
                    ...res,
                    { errorCode: item.value?.message, formElementRef: item.value?.ref },
                ];
            }, []);
            return [...acc, ...arrayErrors];
        } else {
            return [...acc, { errorCode: value?.message, formElementRef: value?.ref }];
        }
    }, []);
};

export const orderFormErrors = (formErrors: FieldErrors<FieldValues>, orderList: string[]) => {
    if (!formErrors || !orderList.length) {
        return formErrors;
    }
    return orderList.reduce((acc, curr) => {
        if (formErrors[curr]) {
            return {
                ...acc,
                [curr]: formErrors[curr],
            };
        }
        return acc;
    }, {});
};

const appendObjectPropertyNames = <T = { [key: string]: string }>(obj: T, appendText: string) => {
    if (!obj) {
        return obj;
    }
    const newObject = {} as any;
    Object.getOwnPropertyNames(obj).forEach(key => {
        newObject[key + appendText] = (obj as any)[key];
    });
    return newObject as T;
};

const mapFormToPageErrorsByPriority = (
    fieldErrors: FieldErrors<FieldValues>,
    errorKeysPriority?: string[]
): PageError[] => {
    const asArray = (errorsObj: FieldErrors<FieldValues>): PageError[] =>
        (mapFormToPageErrors({
            ...appendObjectPropertyNames(errorsObj, 'forVehiclePermit'),
        }) || []) as PageError[];

    // if no keys provided or empty, do not prioritize
    if (!errorKeysPriority || errorKeysPriority.length === 0) {
        return asArray(fieldErrors);
    }

    const pick = (keys: string[]) => {
        const picked: Record<string, unknown> = {};
        keys.forEach(k => {
            if ((fieldErrors as any)?.[k]) {
                picked[k] = (fieldErrors as any)[k];
            }
        });
        return picked as FieldErrors<FieldValues>;
    };

    const pickedInOrder = pick(errorKeysPriority);

    // remaining errors that weren't in the priority list
    const remainingKeys = Object.keys(fieldErrors || {}).filter(
        k => !errorKeysPriority.includes(k)
    );
    const remaining = pick(remainingKeys);

    return [...asArray(pickedInOrder), ...asArray(remaining)];
};

const pluralize = (val: number, word: string) => {
    if (val < 0) {
        return '';
    }
    const singularText = `${val} ${word}`;
    if (val > 1) {
        return `${singularText}s`;
    }
    return singularText;
};

const readFileDataAsBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
            resolve((event?.target?.result as string)?.replace(/data:(?<=)(.*);base64,/, ''));
        };

        reader.onerror = err => {
            reject(err);
        };

        reader.readAsDataURL(file);
    });
};

const isProductSubscribed = (productType: ServiceType, subscribedProducts: ServiceType[]) =>
    subscribedProducts.includes(productType);

const createContact = (type: string, value: string) => {
    return {
        phoneNumber: value,
        phoneNumberType: type,
    };
};

// for objects, this function will only unformat 1st level attributes
const unFormat = <T = string | Record<string, unknown>>(input?: T) => {
    const unFormatRegex = /[/-\s()[\]]/g;
    if (typeof input === 'string') {
        return input ? input.replaceAll(unFormatRegex, '') : input;
    }

    if (input) {
        const unFormattedObj = {} as any;
        Object.getOwnPropertyNames(input).forEach(key => {
            const value = input as any;
            unFormattedObj[key] =
                value[key] && typeof value[key] === 'string'
                    ? (value[key] as string).replaceAll(unFormatRegex, '')
                    : value[key];
        });
        return unFormattedObj as T;
    }
    return input;
};

const getRegisteredProducts = (products?: Products) => {
    const registeredProducts: ServiceType[] = [];

    if (!products) return [];
    if (products.driverLicense?.number) {
        registeredProducts.push('DLRA');
    }
    if (products.licencePlates?.every(lPlate => lPlate.number)) {
        registeredProducts.push('VLRA');
    }
    if (products.healthCard?.number) {
        registeredProducts.push('HCRA');
    }
    if (products.photoCard?.number) {
        registeredProducts.push('OPRA');
    }

    return registeredProducts;
};

const mapVpToDetailsForm = (vp: VehiclePermit): ProductDetailsFormState | undefined => {
    return {
        driversLicenseNumber: '',
        postalCode: vp.postalCode,
        driversLicenseIssueDateYear: 0,
        driversLicenseIssueDateMonth: 0,
        driversLicenseIssueDateDay: 0,
        healthCardNumber: '',
        healthCardVersionCode: '',
        healthCardDocumentControlNumber: '',
        photoCardNumber: '',
        licensePlateNumber: '',
        permitNumber: '',
        rin: vp.rin,
        hasDriversLicense: 'no',
        hasVehiclePermit: 'yes',
    };
};

const mapOPCToDetailsForm = (
    ontarioPhotoCard: PhotoCardFormState | undefined
): PhotoCardFormState | undefined => {
    return {
        ontarioPhotoCardIssueDateDay: ontarioPhotoCard?.ontarioPhotoCardIssueDateDay || 0,
        ontarioPhotoCardIssueDateMonth: ontarioPhotoCard?.ontarioPhotoCardIssueDateMonth || 0,
        ontarioPhotoCardIssueDateYear: ontarioPhotoCard?.ontarioPhotoCardIssueDateYear || 0,
        ontarioPhotoCardNumber: ontarioPhotoCard?.ontarioPhotoCardNumber || '',
        postalCode: ontarioPhotoCard?.postalCode || '',
    };
};

const mapDlToDetailsForm = (driversLicence: DriverLicense): ProductDetailsFormState | undefined => {
    return {
        driversLicenseNumber: driversLicence.number,
        postalCode: driversLicence.postalCode,
        driversLicenseIssueDateYear: 0,
        driversLicenseIssueDateMonth: 0,
        driversLicenseIssueDateDay: 0,
        healthCardNumber: '',
        healthCardVersionCode: '',
        healthCardDocumentControlNumber: '',
        photoCardNumber: '',
        licensePlateNumber: '',
        rin: '',
        hasDriversLicense: driversLicence?.number ? 'yes' : 'no',
    };
};

const mapHCToDetailsForm = (healthCard: HealthCard): ProductDetailsFormState | undefined => {
    return {
        driversLicenseNumber: '',
        postalCode: '',
        driversLicenseIssueDateYear: 0,
        driversLicenseIssueDateMonth: 0,
        driversLicenseIssueDateDay: 0,
        healthCardNumber: healthCard?.number,
        healthCardVersionCode: healthCard?.versionCode,
        healthCardDocumentControlNumber: healthCard?.controlNumber,
        photoCardNumber: '',
        licensePlateNumber: '',
        rin: '',
        hasDriversLicense: 'no',
    };
};

const mapDlOrPcDetails = (dlOrPcDetails: ProductDetailsFormState, subscribeRR = true) => {
    return {
        number: dlOrPcDetails.driversLicenseNumber
            ? unFormat(dlOrPcDetails.driversLicenseNumber)
            : unFormat(dlOrPcDetails.photoCardNumber),
        postalCode: unFormat(dlOrPcDetails.postalCode),
        productId: 0,
        subscribeRR,
    };
};

const mapHC = (hcDetails: ProductDetailsFormState, subscribeRR = true) => {
    const hcNumber = unFormat(hcDetails.healthCardNumber);
    return {
        number: hcNumber?.substring(0, hcNumber.length - 2),
        versionCode: hcNumber?.substring(hcNumber.length - 2),
        controlNumber: hcDetails.healthCardDocumentControlNumber,
        postalCode: unFormat(hcDetails.postalCode),
        dateOfBirth: `${hcDetails.dateOfBirthYear}-${hcDetails.dateOfBirthMonth}-${hcDetails.dateOfBirthDay}`,
        productId: 0,
        subscribeRR,
    };
};

const mapLP = (lpDetails: ProductDetailsFormState, subscribeRR = true) => {
    return {
        number: unFormat(lpDetails.licensePlateNumber),
        permitNumber: unFormat(lpDetails.permitNumber),
        registrantIdentificationNumber: lpDetails.rin ?? null,
        productId: 0,
        subscribeRR,
    };
};

const mapFormDataToIACValidation = (formData: FormDataValidation) => {
    const ret = [];
    const { healthCard, vehiclePermit, accessibleParkingPermit, ontarioPhotoCard } = formData;

    if (healthCard) {
        const healthCardNum = (healthCard?.healthCardNumber || '')?.replaceAll(/[-\s]/g, '') || '';
        const healthCardNumber = (healthCardNum.match(/\d+/g) || [])[0];
        const healthCardVersionCode = (healthCardNum.match(/[A-Z-a-z]+/g) || [])[0]?.toUpperCase();
        const lastPostalCode = (healthCard?.postalCode?.replace(/\s+/g, '') || '').toUpperCase();

        if (healthCard?.healthCardType === ProductTypeEnum.PHOTO_HEALTH_CARD) {
            ret.push({
                type: 'PhotoCard',
                healthCardNumber,
                healthCardVersionCode,
                healthCardDocumentControlNumber:
                    healthCard?.healthCardDocumentControlNumber?.toUpperCase(),
                lastPostalCode,
            });
        } else {
            ret.push({
                type: 'RedAndWhiteCard',
                healthCardNumber,
                healthCardVersionCode,
                healthCardType: 'RedAndWhiteCard',
                lastPostalCode,
            });
        }
    }

    if (vehiclePermit) {
        if (
            vehiclePermit?.productType === ProductTypeEnum.DRIVERS_LICENSE ||
            vehiclePermit?.haveDriversLicense === 'yes'
        ) {
            ret.push({
                type: 'DriverLicense',
                driversLicenseNumber: (
                    (vehiclePermit?.driversLicenseNumber || '')?.replaceAll(/[-\s]/g, '') || ''
                ).toUpperCase(),
                driversLicenseIssueDateYear:
                    Number(vehiclePermit?.driversLicenseIssueDateYear) || 0,
                driversLicenseIssueDateMonth:
                    Number(vehiclePermit?.driversLicenseIssueDateMonth) || 0,
                driversLicenseIssueDateDay: Number(vehiclePermit?.driversLicenseIssueDateDay),
                lastPostalCode: (vehiclePermit?.postalCode || '')
                    ?.replace(/\s+/g, '')
                    .toUpperCase(),
            });
        }
        if (
            vehiclePermit?.productType === ProductTypeEnum.VEHICLE_PERMIT ||
            vehiclePermit?.haveVehiclePermit === 'yes'
        ) {
            ret.push({
                type: 'RIN',
                rin: vehiclePermit?.rin,
                lastPostalCode: (vehiclePermit?.postalCode || '')
                    ?.replace(/\s+/g, '')
                    .toUpperCase(),
            });
        }
    }

    if (ontarioPhotoCard && vehiclePermit?.productType === ProductTypeEnum.ONTARIO_PHOTO_CARD) {
        ret.push({
            type: 'OPC',
            opcNumber: (
                (ontarioPhotoCard?.ontarioPhotoCardNumber || '')?.replaceAll(/[-\s]/g, '') || ''
            ).toUpperCase(),
            opcIssueDateYear: Number(ontarioPhotoCard?.ontarioPhotoCardIssueDateYear) || 0,
            opcIssueDateMonth: Number(ontarioPhotoCard?.ontarioPhotoCardIssueDateMonth) || 0,
            opcIssueDateDay: Number(ontarioPhotoCard?.ontarioPhotoCardIssueDateDay),
            lastPostalCode: (ontarioPhotoCard?.postalCode || '')?.replace(/\s+/g, '').toUpperCase(),
        });
    }

    if (accessibleParkingPermit) {
        ret.push({
            type: 'APP',
            lastPostalCode: (
                accessibleParkingPermit?.lastPostalCode ||
                healthCard?.postalCode ||
                ''
            )
                .replace(/\s+/g, '')
                .toUpperCase(),
            appPermitNumber: accessibleParkingPermit?.permitNumber,
            appIdentificationNumber: accessibleParkingPermit?.identificationNumber,
            dateOfBirth: `${accessibleParkingPermit?.birthYear}-${accessibleParkingPermit?.birthMonth?.padStart(2, '0')}-${accessibleParkingPermit?.birthDay?.padStart(2, '0')}`,
            gender: accessibleParkingPermit?.gender,
            contactInfo: {
                individualName: {
                    firstName: accessibleParkingPermit?.firstName,
                    middleName: accessibleParkingPermit?.middleName,
                    lastName: accessibleParkingPermit?.lastName,
                },
            },
        });
    }

    return ret;
};

const mapFormDataToIACUpdate = (updateData: FormDataUpdate) => {
    return {
        requestId: updateData?.validationResult?.requestId || '',
        mohTransactionId: updateData?.validationResult?.mohTransactionId || '',
        mtoTransactionId: updateData?.validationResult?.mtoTransactionId || '',
        cxpTransactionId: updateData?.validationResult?.cxpTransactionId || '',
        appTransactionId: updateData?.validationResult?.appTransactionId || '',
        dateOfBirth: updateData?.validationResult?.dob || '',
        residentialAddress: updateData?.residentialAddress,
        mailingAddress: updateData?.mailingAddress,
        contactInfo: updateData?.contactInfo,
        healthNumber: (
            ((updateData?.healthCard?.healthCardNumber || '').match(/\d+/g) || [])[0] || ''
        ).toUpperCase(),
        driverLicenseNumber: (
            (updateData?.vehiclePermit?.driversLicenseNumber || '')?.replaceAll(/[-\s]/g, '') || ''
        ).toUpperCase(),
        driverLicenseIssueDate: updateData?.vehiclePermit?.driversLicenseIssueDateYear
            ? `${updateData?.vehiclePermit?.driversLicenseIssueDateYear}-${updateData?.vehiclePermit?.driversLicenseIssueDateMonth}-${updateData?.vehiclePermit?.driversLicenseIssueDateDay}`
            : '',
        rin: updateData?.vehiclePermit?.rin || '',
        ontarioPhotoCardNumber: updateData?.ontarioPhotoCard?.ontarioPhotoCardNumber || '',
        prevPostalCode: (updateData?.healthCard?.postalCode || '')
            .replaceAll(' ', '')
            .toUpperCase(),
        isNorthernResident: updateData?.isNorthernResident,
        sameAsResidential: updateData?.sameAsResidential,
        hasFalseInfoConsent: updateData?.hasFalseInfoConsent,
        cards: mapFormDataToIACValidation(updateData) || [],
    };
};

const mapFormDataToIACProductsUpdate = (updateData: FormDataUpdate) => {
    return {
        approval: updateData?.accepted ?? false,
        cxpTransactionId: updateData?.validationResult?.cxpTransactionId || '',
        dateOfBirth: updateData?.validationResult?.dob || '',
        residentialAddress: updateData?.residentialAddress,
        cards: mapFormDataToIACValidation(updateData) || [],
    };
};

const mapFormDataToIACPrint = (printData: Partial<AddressChange>) => {
    const cardTypes = [];

    if (
        printData?.accessibleParkingPermit?.permitNumber &&
        !printData?.addressChangeResp?.errorCodeAPP
    ) {
        cardTypes.push('APP');
    }

    if (printData?.healthCard?.healthCardNumber && !printData?.addressChangeResp?.errorCodeMOH) {
        if (printData?.healthCard?.healthCardType === 'PhotoHealthCard') {
            cardTypes.push('PhotoCard');
        }
        if (printData?.healthCard?.healthCardType === 'RedWhiteHealthCard') {
            cardTypes.push('RedAndWhiteCard');
        }
    }
    if (
        printData?.ontarioPhotoCard?.ontarioPhotoCardNumber &&
        !printData?.addressChangeResp?.errorCodeMTO
    ) {
        cardTypes.push('OPC');
    }
    if (
        printData?.addressChangeResp?.success !== false &&
        printData?.addressChangeResp?.products &&
        printData?.addressChangeResp?.products?.length > 1
    ) {
        if (printData?.vehiclePermit && !printData?.addressChangeResp?.errorCodeMTO) {
            if (printData?.vehiclePermit?.driversLicenseNumber) {
                cardTypes.push('DriverLicense');
            }
            if (printData?.vehiclePermit?.rin) {
                cardTypes.push('RIN');
            }
        }
    }

    return {
        requestId: printData?.validationResult?.requestId || '',
        appTransactionId: printData?.validationResult?.appTransactionId || '',
        mohTransactionId: printData?.validationResult?.mohTransactionId || '',
        mtoTransactionId: printData?.validationResult?.mtoTransactionId || '',
        cxpTransactionId: printData?.validationResult?.cxpTransactionId || '',
        sameAsResidential: printData?.sameAsResidential,
        residentialAddress: printData?.residentialAddress,
        mailingAddress: printData?.mailingAddress,
        cardTypes,
    };
};

const mapFormDataToIACEmail = (emailData: Partial<AddressChange>, formData: any) => {
    const email = formData?.email;
    const cardTypes = [];

    if (
        emailData?.accessibleParkingPermit?.permitNumber &&
        !emailData?.addressChangeResp?.errorCodeAPP
    ) {
        cardTypes.push('APP');
    }

    if (emailData?.healthCard?.healthCardNumber && !emailData?.addressChangeResp?.errorCodeMOH) {
        if (emailData?.healthCard?.healthCardType === 'PhotoHealthCard') {
            cardTypes.push('PhotoCard');
        }
        if (emailData?.healthCard?.healthCardType === 'RedWhiteHealthCard') {
            cardTypes.push('RedAndWhiteCard');
        }
    }
    if (
        emailData?.ontarioPhotoCard?.ontarioPhotoCardNumber &&
        !emailData?.addressChangeResp?.errorCodeMTO
    ) {
        cardTypes.push('OPC');
    }
    if (emailData?.vehiclePermit && !emailData?.addressChangeResp?.errorCodeMTO) {
        if (emailData?.vehiclePermit?.driversLicenseNumber) {
            cardTypes.push('DriverLicense');
        }
        if (emailData?.vehiclePermit?.rin) {
            cardTypes.push('RIN');
        }
    }

    return {
        email,
        requestId: emailData?.validationResult?.requestId || '',
        appTransactionId: emailData?.validationResult?.appTransactionId || '',
        mohTransactionId: emailData?.validationResult?.mohTransactionId || '',
        mtoTransactionId: emailData?.validationResult?.mtoTransactionId || '',
        cxpTransactionId: emailData?.validationResult?.cxpTransactionId || '',
        sameAsResidential: emailData?.sameAsResidential,
        residentialAddress: emailData?.residentialAddress,
        mailingAddress: emailData?.mailingAddress,
        cardTypes,
    };
};

const hasSuccessUpdate = (resp: any) =>
    (!!resp?.appResponse && !resp?.cxpAppErrorCode) ||
    (!!resp?.mohResponse && !resp?.cxpMOHErrorCode) ||
    (!!resp?.mtoResponse && !resp?.cxpMTOErrorCode);

const mapProductsToResp = (data: FormDataUpdate, resp: any) => {
    const ret: AddressChangeResp = {
        success: hasSuccessUpdate(resp),
        errorCodeMOH: resp?.cxpMOHErrorCode,
        errorCodeMTO: resp?.cxpMTOErrorCode,
        errorCodeAPP: resp?.cxpAppErrorCode,
        warningCodeMOH: resp?.cxpMOHWarningCode,
        warningCodeMTO: resp?.cxpMTOWarningCode,
        warningCodeAPP: resp?.cxpAppWarningCode,
        errorType: resp?.errorType,
        transactionID: data?.validationResult?.cxpTransactionId || '',
        products: [],
    };

    if (data?.healthCard) {
        ret.products.push({
            type: 'healthCard',
            number: data?.healthCard?.healthCardNumber || '',
            status: !!resp?.mohResponse && !resp?.cxpMOHErrorCode,
            errorCode: resp?.cxpMOHErrorCode,
        });
    }
    if (data?.vehiclePermit?.rin) {
        ret.products.push({
            type: 'vehiclePermit',
            number: data?.vehiclePermit?.rin || '',
            status: !!resp?.mtoResponse && !resp?.cxpMTOErrorCode,
            errorCode: resp?.cxpMTOErrorCode,
        });
    }
    if (data?.vehiclePermit?.driversLicenseNumber) {
        ret.products.push({
            type: 'driverLicense',
            number: data?.vehiclePermit?.driversLicenseNumber || '',
            status: !!resp?.mtoResponse && !resp?.cxpMTOErrorCode,
            errorCode: resp?.cxpMTOErrorCode,
        });
    }
    if (data?.ontarioPhotoCard) {
        ret.products.push({
            type: 'ontarioPhotoCard',
            number: data?.ontarioPhotoCard?.ontarioPhotoCardNumber || '',
            status: !!resp?.mtoResponse && !resp?.cxpMTOErrorCode,
            errorCode: resp?.cxpMTOErrorCode,
        });
    }
    if (data?.accessibleParkingPermit) {
        ret.products.push({
            type: 'accessibleParkingPermit',
            number: data?.accessibleParkingPermit?.permitNumber || '',
            status: !!resp?.appResponse && !resp?.cxpAppErrorCode,
            errorCode: resp?.cxpAppErrorCode,
        });
    }

    return ret;
};

const mapServiceWitIdType = (serviceType: ServiceType) => {
    switch (serviceType) {
        case 'DLRA':
            return 'settings.driverLicense';
        case 'VLRA':
            return 'settings.licensePlate';
        case 'HCRA':
            return 'settings.healthCard';
        case 'OPRA':
            return 'settings.ontarioPhotoCard';
        default:
            return '';
    }
};

const setLocalStorageWithExpiry = (key: string, value: boolean) => {
    const now = new Date();
    const item = {
        value,
        expiry: now.getTime() + 10 * 365 * 24 * 60 * 60 * 1000, // Convert minutes to milliseconds
    };
    localStorage.setItem(key, JSON.stringify(item));
};

const getLocalStorageWithExpiry = (key: string) => {
    const itemString = localStorage.getItem(key);
    if (!itemString) {
        return null;
    }

    const item = JSON.parse(itemString);
    const now = new Date();

    if (now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
    }

    return item.value;
};

const buildErrorMsgInterpolationOptions = (
    errorCodeAndParams: string,
    separator = ','
): TOptions => {
    let options = {};

    const errorCodeAndParamsParts = errorCodeAndParams?.split(separator);
    if (errorCodeAndParamsParts?.length > 1) {
        errorCodeAndParamsParts.forEach((messagePart, index) => {
            if (index > 0) {
                const paramParts = messagePart.trim().split(':');
                options = {
                    ...options,
                    ...(paramParts.length === 1
                        ? { [paramParts[0].trim()]: paramParts[0].trim() }
                        : { [paramParts[0].trim()]: paramParts[1].trim() }),
                };
            }
        });
        return options;
    }
    return options;
};

const getLocaleForDateFormat = (locale?: string) => {
    switch (locale) {
        case 'en':
        default:
            return enCA;
        case 'fr':
            return frCA;
    }
};

const formatMessageDate = (messageDate: string, locale?: string) => {
    if (!messageDate) {
        return messageDate;
    }

    const date = new Date(messageDate);
    const diffInWeeks = differenceInWeeks(new Date(), date);
    if (isToday(date)) {
        return format(date, 'hh:mm aaa', { locale: getLocaleForDateFormat(locale) });
    }
    if (diffInWeeks < 1) {
        return format(date, 'MMM d', { locale: getLocaleForDateFormat(locale) });
    }

    return format(date, 'yyyy/MM/dd', { locale: getLocaleForDateFormat(locale) });
};

type ValidatorFunction = (char: string) => boolean;

type FormatterConfig = {
    data: string;
    patternSchema: string;
    previousData?: string | null | undefined;
    ignoreCase?: boolean | null | undefined;
    ignoreMaxLength?: boolean | null | undefined;
};

function patternDataFormatter(config: FormatterConfig): string {
    let formattedData = '';
    let inputIndex = 0;
    const newData: string = config.data;

    const inputValidators: Record<string, ValidatorFunction> = {
        D: (char: string): boolean => /[A-Za-z]/.test(char),
        X: (char: string): boolean => /\d/.test(char),
    };
    const applyConfigOptions = (): void => {
        if (!config.ignoreCase) {
            formattedData = formattedData.toUpperCase();
        }
        if (!config.ignoreMaxLength) {
            formattedData = formattedData.slice(0, config.patternSchema.length);
        }
    };
    const skipInvalidCharacter = (char: string): void => {
        if (!INVALID_CHARACTERS_REGEX.test(char)) {
            while (
                inputIndex < newData.length &&
                INVALID_CHARACTERS_REGEX.test(newData[inputIndex])
            ) {
                inputIndex++;
            }
        }
    };
    const isDeleting: boolean = !!(
        config?.previousData && newData.length < config?.previousData?.length
    );

    for (const pattern of config.patternSchema) {
        if (inputValidators[pattern]) {
            skipInvalidCharacter(pattern);
            if (inputIndex >= newData.length) {
                break;
            }
            const currentCharacter = newData[inputIndex];
            if (!inputValidators[pattern](currentCharacter)) {
                if (newData[inputIndex - 1] === newData[inputIndex]) {
                    formattedData = newData.substring(0, inputIndex);
                    applyConfigOptions();
                    return formattedData;
                } else {
                    formattedData = <string>newData;
                    applyConfigOptions();
                    return formattedData;
                }
            }
            formattedData += currentCharacter;
            inputIndex++;
        } else {
            skipInvalidCharacter(pattern);
            if (inputIndex < newData.length && newData[inputIndex] === pattern) {
                formattedData += pattern;
                inputIndex++;
            } else if (!isDeleting) {
                formattedData += pattern;
            }
        }
    }

    if (inputIndex < newData.length) {
        formattedData += newData.slice(inputIndex);
    }
    applyConfigOptions();
    return formattedData;
}

const formatDriversLicense = (
    strInput: string,
    format: string = DRIVER_LICENSE_PATTERN,
    previousInput?: string | null | undefined
) => {
    return patternDataFormatter({
        data: strInput,
        patternSchema: format,
        previousData: previousInput,
        ignoreMaxLength: true,
    });
};

const formatOntarioPhotoCard = (
    strInput: string,
    format: string = PHOTO_CARD_PATTERN,
    previousInput?: string | null | undefined
) => {
    return patternDataFormatter({
        data: strInput,
        patternSchema: format,
        previousData: previousInput,
        ignoreMaxLength: true,
    });
};

const formatPostalCode = (strInput: string, format = POSTAL_CODE_FORMAT) => {
    return strInput.replace(/[-\s]/g, '').replace(format, '$1 $2').toUpperCase();
};

const formatPhotoHealthCard = (
    strInput: string,
    format: string = HEALTH_CARD_PATTERN,
    previousInput?: string | null | undefined
) => {
    return patternDataFormatter({
        data: strInput,
        patternSchema: format,
        previousData: previousInput,
        ignoreMaxLength: true,
    });
};

export const formatPhoneNumber = (strInput: string, format = CELLPHONE_REGEX) => {
    const cleaned = strInput.replace(/\D/g, '');
    const match = cleaned.match(format);
    if (match?.length) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return strInput;
};

export const formatOffenceNumber = (strInput: string, format = OFFENCE_NUMBER_FORMAT_REGEX) => {
    const cleaned = strInput.replace(/[-\s]/g, '');
    const match = cleaned.match(format);
    if (match?.length) {
        let result = `${match[1]}`;
        if (match[2]) result += `-${match[2]}`;
        if (match[3]) result += `-${match[3]}`;
        if (match[4]) result += `-${match[4]}`;
        if (match[5]) result += `-${match[5]}`;
        return result.toUpperCase();
    } else {
        return cleaned;
    }
};

const isInRange = (value: string, from: string, to: string) => {
    const numericValue = parseInt(value, 10);
    const numericFrom = parseInt(from, 10);
    const numericTo = parseInt(to, 10);

    if (!Number.isNaN(numericFrom) && !Number.isNaN(numericTo) && !Number.isNaN(numericValue)) {
        return numericValue >= numericFrom && numericValue <= numericTo;
    }

    return false;
};

/* eslint-disable  no-restricted-syntax */
const isFormDataModified = (data: TPCAddressInfo, selectedAddress: TPCAddressInfo): boolean => {
    // for type 1 and 2, only street name and street number are mandatory fields
    const requiredFields = ['streetName', 'streetNumber'];

    for (const k of Object.keys(data)) {
        let dataK = data[k as keyof TPCAddressInfo];
        let selectedK = selectedAddress[k as keyof TPCAddressInfo];
        if (typeof dataK === 'string') {
            dataK = dataK.trim();
            selectedK = String(selectedK || '').trim();
        }
        if (requiredFields.includes(k) && dataK !== selectedK) {
            if (
                !(
                    k === 'streetNumber' &&
                    data.streetNumber &&
                    !data.noStreetName &&
                    isInRange(
                        data.streetNumber || '',
                        selectedAddress.streetFromNumber || '',
                        selectedAddress.streetToNumber || ''
                    )
                )
            ) {
                return true;
            }
        }
    }

    return false;
};

const getValidationSourceType = (
    data: TPCAddressInfo,
    selectedAddress: TPCAddressInfo
): 'CP' | 'CL' => {
    if (Object.keys(selectedAddress).length === 0) {
        return 'CP';
    }
    if (data.noStreetName) {
        return 'CL';
    }
    if (selectedAddress?.typeOfAddress === 1 || selectedAddress?.typeOfAddress === 2) {
        if (isFormDataModified(data, selectedAddress)) {
            return 'CL';
        }
        return 'CP';
    }

    return 'CL';
};

const needShowErrorPage = (errorCode: string) => {
    return HARDSTOP_ERROR_CODES.includes(errorCode);
};

const needShowContinue = (errorCode: string) => {
    return ['CXP012'].includes(errorCode);
};

const currencyFormatter = (locale: string = defaultLocale) =>
    Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'CAD',
    });

const initAddressInfo = {
    routeType: '',
    address2: '',
    address1: '',
    poBoxKeyword: '',
    poBoxNumber: '',
    poBoxFromNumber: '',
    poBoxToNumber: '',
    postalCode: '',
    streetFromSuffix: '',
    streetToSuffix: '',
    suiteFromNumber: '',
    suiteToNumber: '',
    streetName: '',
    streetType: '',
    streetNumber: '',
    streetNumberSuffix: '',
    streetDirection: '',
    routeNumber: '',
    addressLine1: '',
    municipality: '',
    province: '',
    streetFromNumber: '',
    streetToNumber: '',
    stationType: '',
    stationName: '',
    typeOfAddress: 0,
    directoryArea: '',
    suiteNumber: '',
    deliveryInstallationType: '',
    deliveryInstallationName: '',
    deliveryInstallationArea: '',
    unitType: '',
    unitNumber: '',
    lotType: '',
    lotNumber: '',
    propertyIdentifierOption: '',
    concessionNumber: '',
    planNumber: '',
    township: '',
    noStreetName: false,
    sameMailingAsResidential: false,
    validationSourceTypeCode: '',
    addressLineSearch: '',
    addressLineMailingSearch: '',
    sameAsResidential: false,
    pcLookType: 0,
};

interface NameMaps {
    unitNumber: number;
    streetNumber: number;
    streetNumberSuffix: number;
    streetName: number;
    routeNumber: number;
    lotNumber: number;
    concessionNumber: number;
    planNumber: number;
    township: number;
}

const verifyInputLength = (name: string, value: string | number) => {
    const nameMaps: NameMaps = {
        unitNumber: 6,
        streetNumber: 6,
        streetNumberSuffix: 6,
        streetName: 30,
        routeNumber: 4,
        lotNumber: 5,
        concessionNumber: 10,
        planNumber: 6,
        township: 30,
    };

    const length = nameMaps[name as keyof NameMaps];
    if (!length) {
        return true;
    }

    return typeof value === 'number' ? String(value).length <= length : value.length <= length;
};

const htmlToPrint = (htmlContent: any) => {
    let printWindow = window.open('', '_blank');

    if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        // printWindow.print();
        // printWindow.onafterprint = () =>  printWindow.close();
    }
};

const createBlobForBase64Content = (content: string, mimeType: string = 'application/pdf') => {
    const prefix = `data:${mimeType};base64,`;
    const base64WithoutPrefix = content.startsWith(prefix)
        ? content.substring(prefix.length)
        : content;
    const bytes = atob(base64WithoutPrefix);
    let byteLength = bytes.length;
    let byteArray = new Uint8Array(byteLength);

    while (byteLength--) {
        byteArray[byteLength] = bytes.charCodeAt(byteLength);
    }
    return new Blob([byteArray], { type: mimeType });
};

const createBlobUrlForBase64Pdf = (content: string, mimeType?: string) => {
    return createObjectURL(createBlobForBase64Content(content, mimeType));
};

const createObjectURL = (blob: Blob) => {
    return URL.createObjectURL(blob);
};

const convertFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${value} ${sizes[i]}`;
};

const openBlobOrDocument = (content: string, type: 'text/html' | 'application/pdf' | 'url') => {
    if (content && type) {
        switch (type) {
            case 'url':
                window.open(content, '_blank');
                break;
            case 'application/pdf':
                const docURL = createBlobUrlForBase64Pdf(content);
                window.open(docURL, '_blank');
                break;
            default: {
                const docURL = URL.createObjectURL(new Blob([content], { type }));
                window.open(docURL, '_blank');
            }
        }
    }
};

const downloadPdf = (base64Content: string, fileName: string) => {
    if (!base64Content) {
        return;
    }
    const docURL = createBlobUrlForBase64Pdf(base64Content);
    const downloadLink = document.createElement('a');
    downloadLink.href = docURL;
    downloadLink.download = fileName ?? 'ServiceOntario-renewal-receipt.pdf';
    downloadLink.click();
};

const downloadPdfFromBlob = (blob: Blob, fileName: string) => {
    const anchor = document.createElement('a');

    anchor.href = window.URL.createObjectURL(blob);
    anchor.download = fileName;
    anchor.hidden = true;

    document.body.append(anchor);
    anchor.click();
    anchor.remove();

    window.addEventListener(
        'focus',
        () => {
            URL.revokeObjectURL(anchor.href);
        },
        { once: true }
    );
};

const validateIssueDate = (
    year: string | number,
    month: string | number,
    day: string | number,
    validationOptions?: {
        futureMessageCode?: string;
        invalidDateCode?: string;
        requiredValueCode?: string;
    }
) => {
    const INVALID_DATE_MESSAGE_CODE = validationOptions?.invalidDateCode || 'CXPFE0062';
    const FUTURE_MESSAGE_CODE = validationOptions?.futureMessageCode || 'CXPFE0062';
    const REQUIRED_VALUE_MESSAGE_CODE = validationOptions?.futureMessageCode || 'CXPFE0032';
    let flag = '';
    let ret =
        Number(year) > 0 && Number(year) <= new Date().getFullYear() ? true : FUTURE_MESSAGE_CODE;
    if (ret === true) {
        if (!month) {
            ret = REQUIRED_VALUE_MESSAGE_CODE;
        } else {
            ret = Number(month) > 0 && Number(month) <= 12 ? true : INVALID_DATE_MESSAGE_CODE;
        }
        if (ret === true) {
            if (!day) {
                ret = REQUIRED_VALUE_MESSAGE_CODE;
            } else {
                ret =
                    Number(day) > 0 && Number(day) <= 31
                        ? new Date(Number(year), Number(month) - 1, Number(day)) > new Date()
                            ? FUTURE_MESSAGE_CODE
                            : true
                        : INVALID_DATE_MESSAGE_CODE;
            }
            if (ret !== true) {
                flag = 'day';
            }
        } else {
            flag = 'month';
        }
    } else {
        flag = 'year';
    }

    return { ret, flag };
};

const validateIssueDateIAC = (
    year: string | number,
    month: string | number,
    day: string | number,
    validationOptions?: {
        invalidFormatMessageCode?: string;
        isFutureDateMessageCode?: string;
        isExpiredMessageCode?: string;
        requiredValueMessageCode?: string;
    }
) => {
    const INVALID_DATE_MESSAGE_CODE = validationOptions?.invalidFormatMessageCode || 'CXPIAC008';
    const FUTURE_DATE_MESSAGE_CODE = validationOptions?.isFutureDateMessageCode || 'CXPIAC009';
    const EXPIRED_MESSAGE_CODE = validationOptions?.isExpiredMessageCode || 'CXPIAC011';
    const REQUIRED_VALUE_MESSAGE_CODE = validationOptions?.requiredValueMessageCode || 'CXPIAC010';

    const trimmedYear = typeof year === 'string' ? year.trim() : String(year ?? '').trim();
    const trimmedMonth = typeof month === 'string' ? month.trim() : String(month ?? '').trim();
    const trimmedDay = typeof day === 'string' ? day.trim() : String(day ?? '').trim();

    let flag: 'year' | 'month' | 'day' | '' = '';

    if (!trimmedYear && !trimmedMonth && !trimmedDay) {
        return { ret: REQUIRED_VALUE_MESSAGE_CODE, flag: 'year' };
    }

    if (!trimmedYear) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'year' };
    }
    if (!trimmedMonth) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'month' };
    }
    if (!trimmedDay) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'day' };
    }

    const y = Number(trimmedYear);
    const m = Number(trimmedMonth);
    const d = Number(trimmedDay);

    if (!Number.isInteger(y) || String(Math.abs(y)).length !== 4) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'year' };
    }
    if (!Number.isInteger(m) || m < 1 || m > 12) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'month' };
    }
    if (!Number.isInteger(d) || d < 1 || d > 31) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'day' };
    }

    const issueDate = new Date(y, m - 1, d);

    if (
        issueDate.getFullYear() !== y ||
        issueDate.getMonth() !== m - 1 ||
        issueDate.getDate() !== d
    ) {
        return { ret: INVALID_DATE_MESSAGE_CODE, flag: 'day' };
    }

    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(todayMidnight.getTime() + (24 * 60 * 60 * 1000 - 1));

    if (issueDate.getTime() > endOfToday.getTime()) {
        return { ret: FUTURE_DATE_MESSAGE_CODE, flag: 'day' };
    }

    const sixYearsAfterIssue = addYears(issueDate, 6);
    if (sixYearsAfterIssue.getTime() <= todayMidnight.getTime()) {
        return { ret: EXPIRED_MESSAGE_CODE, flag: 'day' };
    }

    return { ret: true as const, flag };
};

const maskValue = (value: string | undefined, unmaskSize: number): string | undefined => {
    if (!value || value.length <= unmaskSize) {
        return value;
    }

    const maskedValue = value.slice(-unmaskSize).padStart(value.length, '*');
    return maskedValue;
};

const isMaskedValue = (value: string | undefined): boolean => {
    return !!value?.startsWith('*');
};

const snakeToCamelCase = (value: string) => {
    if (!value) {
        return undefined;
    }

    const snakeLowerCaseInput = value.toLocaleLowerCase();
    let snakeCamelCaseOutput = '';
    let index = 0;
    let isPreviousCharUnderscore = false;
    while (index < value.length) {
        const isUnderscoreChar = snakeLowerCaseInput[index] === '_';
        if (!isUnderscoreChar) {
            snakeCamelCaseOutput += isPreviousCharUnderscore
                ? snakeLowerCaseInput[index].toUpperCase()
                : snakeLowerCaseInput[index];
        }
        isPreviousCharUnderscore = isUnderscoreChar;
        index++;
    }
    return snakeCamelCaseOutput;
};

const toKebabCase = (value: string) => {
    if (!value) return '';
    const kebab = value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    return ['drivers-license', 'driver-license'].includes(kebab) ? 'drivers-licence' : kebab;
};

const generateErrorList = (errs: FieldErrors<FieldValues>) => {
    if (errs && !!Object.keys(errs).length) {
        return Object.keys(errs)
            .map(key => errs[key])
            .filter((fieldErr): fieldErr is FieldErrors<FieldValues> => fieldErr !== undefined)
            .map(fieldErrs => Object.values(fieldErrs))
            .flatMap(values =>
                values
                    .filter(v => v?.ref instanceof HTMLElement || v?.ref === undefined)
                    .map(v => ({
                        errorCode: v?.message ? String(v.message) : 'Unknown error',
                        formElementRef: v?.ref as
                            | HTMLInputElement
                            | HTMLSelectElement
                            | HTMLTextAreaElement
                            | undefined,
                    }))
            );
    }
    return [];
};

export {
    generateErrorList,
    validateIssueDate,
    validateIssueDateIAC,
    htmlToPrint,
    initAddressInfo,
    verifyInputLength,
    shouldShowStatusBanner,
    shouldShowTrilliumBgContainer,
    shouldShowMenuButton,
    shouldHideAppBar,
    mapFormToPageErrors,
    appendObjectPropertyNames,
    mapFormToPageErrorsByPriority,
    pluralize,
    isProductSubscribed,
    createContact,
    getRegisteredProducts,
    mapDlOrPcDetails,
    mapDlToDetailsForm,
    mapOPCToDetailsForm,
    mapVpToDetailsForm,
    mapHCToDetailsForm,
    mapHC,
    mapLP,
    mapServiceWitIdType,
    readFileDataAsBase64,
    setLocalStorageWithExpiry,
    getLocalStorageWithExpiry,
    buildErrorMsgInterpolationOptions,
    formatPostalCode,
    formatMessageDate,
    formatPhotoHealthCard,
    formatOntarioPhotoCard,
    formatDriversLicense,
    mapFormDataToIACValidation,
    mapFormDataToIACUpdate,
    mapFormDataToIACProductsUpdate,
    mapFormDataToIACPrint,
    mapFormDataToIACEmail,
    mapProductsToResp,
    hasSuccessUpdate,
    getValidationSourceType,
    needShowErrorPage,
    needShowContinue,
    currencyFormatter,
    unFormat,
    maskValue,
    createBlobForBase64Content,
    createBlobUrlForBase64Pdf,
    openBlobOrDocument,
    isMaskedValue,
    snakeToCamelCase,
    toKebabCase,
    downloadPdf,
    downloadPdfFromBlob,
    isDashboardPage,
    convertFileSize,
    createObjectURL,
    mapFormToPageErrorsWithArrayValues,
    mapFormToPageErrorsNested,
};
